import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Camera, Check, Loader2, Pencil, ShieldAlert, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StaffAvatar from "@/components/StaffAvatar";
import { CaseStatusBadge, CASE_STATUS_LABELS } from "@/pages/Children";
import OverviewTab from "@/components/child/OverviewTab";
import ReferralTab from "@/components/child/ReferralTab";
import ParentsTab from "@/components/child/ParentsTab";
import SendTab from "@/components/child/SendTab";
import HealthTab from "@/components/child/HealthTab";
import ConsentsTab from "@/components/child/ConsentsTab";
import DocumentsTab from "@/components/child/DocumentsTab";
import AssignmentsTab from "@/components/child/AssignmentsTab";
import AssessmentsTab from "@/components/child/AssessmentsTab";
import PreAssessmentTab from "@/components/child/PreAssessmentTab";
import FeedbackTab from "@/components/child/FeedbackTab";
import CaseSummaryButton from "@/components/child/CaseSummaryButton";
import JourneyPlansTab from "@/components/child/JourneyPlansTab";
import TrainingTab from "@/components/child/TrainingTab";
import ProgressTab from "@/components/child/ProgressTab";
import IncidentsTab from "@/components/child/IncidentsTab";
import FinalAssessmentTab from "@/components/child/FinalAssessmentTab";
import CaseReportTab from "@/components/child/CaseReportTab";
import AuditTab from "@/components/child/AuditTab";
import SpotChecksPanel from "@/components/SpotChecksPanel";
import PlaceholderTab from "@/components/child/PlaceholderTab";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const PLACEHOLDERS = [
  { key: "safeguarding", label: "Safeguarding", phase: "later phase", desc: "Safeguarding case management will be built in a later phase with restricted access.", restricted: true },
];

export default function ChildDetail() {
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const [child, setChild] = useState(null);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [photoConsent, setPhotoConsent] = useState("pending");
  const photoInputRef = useRef(null);

  const canUpdate = hasPermission("children:update");
  const canAssign = hasPermission("assignments:manage");
  const canSeeSpotChecks = hasPermission("spotchecks:read");

  const [schools, setSchools] = useState([]);
  const [schoolDialog, setSchoolDialog] = useState(false);
  const [schoolPick, setSchoolPick] = useState("");
  const [savingSchool, setSavingSchool] = useState(false);

  const openSchoolDialog = async () => {
    setSchoolPick(child.school_id || "");
    setSchoolDialog(true);
    if (!schools.length) {
      try {
        const { data } = await api.get("/schools", { params: { status: "active" } });
        setSchools(data.items || []);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
  };

  const saveSchool = async () => {
    setSavingSchool(true);
    try {
      await api.patch(`/children/${id}`, { school_id: schoolPick });
      toast.success("School updated");
      setSchoolDialog(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSchool(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/children/${id}`);
      setChild(data);
      setPhotoConsent(data.photo_consent_status || "pending");
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-600" data-testid="child-detail-loading">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading case…
      </div>
    );
  }

  if (denied || !child) {
    return (
      <div data-testid="child-access-denied" className="flex flex-col items-center justify-center py-20 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50 mb-6">
          <ShieldAlert className="h-8 w-8 text-red-600" />
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Access denied</h1>
        <p className="text-base text-slate-600 max-w-md mb-8">
          This case does not exist, or you are not authorised to view it. Access to children's records is limited to
          allocated trainers and authorised administrators. This attempt has been recorded.
        </p>
        <Link to="/children" data-testid="child-denied-back" className="inline-flex h-11 items-center rounded-md bg-primary px-6 text-sm font-semibold text-white hover:bg-brand-hover">
          Back to children
        </Link>
      </div>
    );
  }

  const changeStatus = async (status) => {
    try {
      await api.post(`/children/${id}/status`, { status });
      toast.success("Case status updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "child_photo");
    fd.append("child_id", id);
    fd.append("consent_status", photoConsent);
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photograph updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const primaryTrainer = child.active_assignments?.find((a) => a.assignment_type === "primary");
  const isHistoric = child.record_type === "historic";

  return (
    <div data-testid="child-detail-page" className="space-y-6">
      <Link to="/children" data-testid="back-to-children-link" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to children
      </Link>

      {/* Case header */}
      <div className="rounded-lg border border-border bg-white p-5" data-testid="child-header">
        <div className="flex flex-wrap items-start gap-5">
          <div className="relative">
            <StaffAvatar photoId={child.photo_document_id} name={`${child.first_name} ${child.surname}`} size="h-24 w-24" testid="child-photo" />
            {canUpdate && !isHistoric && (
              <>
                <button
                  data-testid="child-photo-upload-button"
                  onClick={() => photoInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white shadow hover:bg-brand-hover transition-colors duration-200"
                  aria-label="Upload child photograph"
                >
                  <Camera className="h-4 w-4" />
                </button>
                <input ref={photoInputRef} type="file" accept="image/*" className="hidden" data-testid="child-photo-input"
                  onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} />
              </>
            )}
          </div>
          <div className="flex-1 min-w-[220px]">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" data-testid="child-name">
              {child.first_name} {child.surname}
              {child.preferred_name && <span className="text-lg font-medium text-slate-500 ml-2">“{child.preferred_name}”</span>}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-semibold text-slate-700 bg-slate-100 rounded px-2 py-0.5" data-testid="child-case-id">{child.case_id}</span>
              <CaseStatusBadge status={child.case_status} testid="child-case-status" />
              {child.age !== null && child.age !== undefined && (
                <span className="text-sm text-slate-600" data-testid="child-age">Age {child.age}</span>
              )}
            </div>
            <div className="mt-3"><CaseSummaryButton child={child} /></div>
            <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
              <div><dt className="text-slate-500 text-xs">School</dt><dd className="font-medium text-slate-800 flex items-center gap-2" data-testid="child-school">{child.school?.name || "—"}
                {canUpdate && !isHistoric && (
                  <button data-testid="edit-school-button" onClick={openSchoolDialog}
                    className="text-primary hover:text-brand-hover transition-colors duration-200" aria-label="Change school">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
              </dd></div>
              <div><dt className="text-slate-500 text-xs">Local authority</dt><dd className="font-medium text-slate-800" data-testid="child-council">{child.council?.name || "—"}</dd></div>
              <div><dt className="text-slate-500 text-xs">Assigned trainer</dt><dd className="font-medium text-slate-800" data-testid="child-trainer">{primaryTrainer?.trainer_name || <span className="text-amber-700">Unassigned</span>}</dd></div>
            </dl>
            {child.readiness && !isHistoric && (
              <div data-testid="readiness-strip"
                className={`mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-md border px-3 py-2 text-xs ${
                  Object.values(child.readiness).every(Boolean)
                    ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"
                }`}>
                {[
                  ["assessment", "Initial assessment"],
                  ["route_plan", "Route plan"],
                  ["risk_assessment", "Risk assessment"],
                  ["trainer", "Trainer assigned"],
                ].map(([key, label]) => (
                  <span key={key} data-testid={`readiness-${key}`}
                    className={`inline-flex items-center gap-1 font-medium ${child.readiness[key] ? "text-emerald-700" : "text-amber-800"}`}>
                    {child.readiness[key] ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                    {label}
                  </span>
                ))}
                {Object.values(child.readiness).every(Boolean) && (
                  <span className="font-semibold text-emerald-800" data-testid="readiness-ready">Ready to start training</span>
                )}
              </div>
            )}
          </div>
          {canUpdate && !isHistoric && (
            <div className="w-full sm:w-64 space-y-1.5">
              <Label className="text-xs">Case status</Label>
              <Select value={child.case_status} onValueChange={changeStatus}>
                <SelectTrigger data-testid="case-status-select" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {Object.entries(CASE_STATUS_LABELS).map(([k, l]) => (
                    <SelectItem key={k} value={k} data-testid={`case-status-option-${k}`}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {canUpdate && (
                <div className="space-y-1.5 pt-1">
                  <Label className="text-xs">Photo consent status</Label>
                  <Select value={photoConsent} onValueChange={setPhotoConsent}>
                    <SelectTrigger data-testid="photo-consent-select" className="h-10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="obtained">Obtained</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                      <SelectItem value="not_required">Not required</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {isHistoric && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="historic-record-banner">
          <p className="font-semibold">Historic archived record</p>
          <p className="mt-1" data-testid="historic-record-details">
            Recorded from pre-system paper records and archived.
            {child.historic_training_period ? ` Training period: ${child.historic_training_period}.` : ""}
            {child.historic_trainer_name ? ` Trainer: ${child.historic_trainer_name}.` : ""}
            {" "}This record is read-only; scanned paper records can be attached on the Documents tab.
          </p>
          {child.historic_summary && (
            <p className="mt-2 whitespace-pre-wrap" data-testid="historic-record-summary">{child.historic_summary}</p>
          )}
        </div>
      )}

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-white border border-border flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="referral" data-testid="tab-referral">Referral</TabsTrigger>
          <TabsTrigger value="parents" data-testid="tab-parents">Parent/Carer</TabsTrigger>
          <TabsTrigger value="send" data-testid="tab-send">SEND &amp; Support</TabsTrigger>
          <TabsTrigger value="health" data-testid="tab-health">Health &amp; Safety</TabsTrigger>
          <TabsTrigger value="consent" data-testid="tab-consent">Consent</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments">Assignments</TabsTrigger>
          <TabsTrigger value="assessment" data-testid="tab-assessment">Assessment</TabsTrigger>
          <TabsTrigger value="pre-assessment" data-testid="tab-pre-assessment">Pre-Assessment</TabsTrigger>
          <TabsTrigger value="journey" data-testid="tab-journey">Route Plan</TabsTrigger>
          <TabsTrigger value="training" data-testid="tab-training">Training</TabsTrigger>
          <TabsTrigger value="progress" data-testid="tab-progress">Progress</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="tab-incidents">Incidents</TabsTrigger>
          <TabsTrigger value="final" data-testid="tab-final">Final Assessment</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger>
          {canSeeSpotChecks && <TabsTrigger value="spotchecks" data-testid="tab-spotchecks">Spot Checks</TabsTrigger>}
          {PLACEHOLDERS.map((p) => (
            <TabsTrigger key={p.key} value={p.key} data-testid={`tab-${p.key}`}>{p.label}</TabsTrigger>
          ))}
          {hasPermission("audit:read") && <TabsTrigger value="audit" data-testid="tab-audit">Audit / History</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="mt-4"><OverviewTab child={child} reload={load} /></TabsContent>
        <TabsContent value="referral" className="mt-4"><ReferralTab child={child} reload={load} /></TabsContent>
        <TabsContent value="parents" className="mt-4"><ParentsTab child={child} /></TabsContent>
        <TabsContent value="send" className="mt-4"><SendTab child={child} reload={load} /></TabsContent>
        <TabsContent value="health" className="mt-4"><HealthTab child={child} reload={load} /></TabsContent>
        <TabsContent value="consent" className="mt-4"><ConsentsTab child={child} /></TabsContent>
        <TabsContent value="documents" className="mt-4"><DocumentsTab child={child} /></TabsContent>
        <TabsContent value="assignments" className="mt-4"><AssignmentsTab child={child} reload={load} canAssign={canAssign} /></TabsContent>
        <TabsContent value="assessment" className="mt-4"><AssessmentsTab child={child} /></TabsContent>
        <TabsContent value="pre-assessment" className="mt-4"><PreAssessmentTab child={child} /></TabsContent>
        <TabsContent value="journey" className="mt-4"><JourneyPlansTab child={child} /></TabsContent>
        <TabsContent value="training" className="mt-4"><TrainingTab child={child} /></TabsContent>
        <TabsContent value="progress" className="mt-4"><ProgressTab child={child} /></TabsContent>
        <TabsContent value="incidents" className="mt-4"><IncidentsTab child={child} /></TabsContent>
        <TabsContent value="final" className="mt-4"><FinalAssessmentTab child={child} /></TabsContent>
        <TabsContent value="reports" className="mt-4"><CaseReportTab child={child} /></TabsContent>
        <TabsContent value="feedback" className="mt-4"><FeedbackTab child={child} /></TabsContent>
        {canSeeSpotChecks && (
          <TabsContent value="spotchecks" className="mt-4"><SpotChecksPanel childId={id} /></TabsContent>
        )}
        {PLACEHOLDERS.map((p) => (
          <TabsContent key={p.key} value={p.key} className="mt-4">
            <PlaceholderTab title={p.label} description={p.desc} phase={p.phase} restricted={p.restricted} />
          </TabsContent>
        ))}
        {hasPermission("audit:read") && (
          <TabsContent value="audit" className="mt-4"><AuditTab child={child} /></TabsContent>
        )}
      </Tabs>

      <Dialog open={schoolDialog} onOpenChange={setSchoolDialog}>
        <DialogContent className="bg-white" data-testid="school-dialog">
          <DialogHeader>
            <DialogTitle>Change school</DialogTitle>
            <DialogDescription>
              Correct the school linked to {child.first_name}'s case. The change is recorded in the audit log.
              To add a new school, use the Schools page first.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>School</Label>
            <Select value={schoolPick} onValueChange={setSchoolPick}>
              <SelectTrigger data-testid="school-select" className="h-11"><SelectValue placeholder="Select a school" /></SelectTrigger>
              <SelectContent className="bg-white">
                {schools.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`school-option-${s.id}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchoolDialog(false)}>Cancel</Button>
            <Button data-testid="school-save-button" disabled={savingSchool || !schoolPick || schoolPick === child.school_id}
              onClick={saveSchool} className="bg-primary hover:bg-brand-hover text-white font-semibold">
              {savingSchool ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save school"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
